import type { ReactNode } from "react"

import { useTranslation } from "react-i18next"

import { AlertCircle, ChevronDown, Gauge, RefreshCw } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

import { fairUseErrorDescription } from "./fair-use-utils"
import { useCollapsibleSection } from "../../hooks/use-collapsible-section"

/**
 * The card frame every state of the AI-budget view shares — loading, error,
 * "not provisioned" and the loaded card alike.
 *
 * One shell means the heading cannot drift between states, and the card never
 * changes size or title while its first page is in flight.
 */
export function FairUseOpsCardShell({
  actions,
  children,
}: {
  actions?: ReactNode
  children: ReactNode
}) {
  const { t } = useTranslation()
  const { isOpen, setIsOpen } = useCollapsibleSection("admin.dashboard.budget", true)

  return (
    <Collapsible asChild open={isOpen} onOpenChange={setIsOpen}>
      <Card className="self-start">
        <CardHeader className="gap-4 pb-4">
          <CollapsibleTrigger asChild>
            <button
              className="focus-visible:ring-ring flex w-full items-start justify-between gap-3 rounded-md text-left focus-visible:ring-2 focus-visible:outline-none"
              type="button"
            >
              <div className="flex min-w-0 items-start gap-2">
                <Gauge className="mt-0.5 size-5 shrink-0" />
                <div className="min-w-0 space-y-1">
                  <CardTitle className="text-lg">
                    {t("adminDashboard.fairUse.ops.title", "AI budget")}
                  </CardTitle>
                  <CardDescription>
                    {t(
                      "adminDashboard.fairUse.ops.caption",
                      "Who is using their monthly AI budget, and requests waiting for you."
                    )}
                  </CardDescription>
                </div>
              </div>
              <ChevronDown
                aria-hidden="true"
                className={cn("mt-1 size-5 shrink-0 transition-transform", isOpen && "rotate-180")}
              />
            </button>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent forceMount className="data-[state=closed]:hidden" hidden={!isOpen}>
          {actions ? <div className="px-6 pb-4">{actions}</div> : null}
          <CardContent className="space-y-4">{children}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}

/** Skeleton shown while the month's first page and summary are in flight. */
export function FairUseOpsLoading() {
  return (
    <FairUseOpsCardShell>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-[74px] w-full rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-8 w-72 rounded-lg" />
      {Array.from({ length: 4 }).map((_, index) => (
        <Skeleton key={index} className="h-12 w-full rounded-lg" />
      ))}
    </FairUseOpsCardShell>
  )
}

/** Fair use genuinely isn't provisioned here (503 `fair_use_not_configured`). */
export function FairUseOpsUnavailable() {
  const { t } = useTranslation()

  return (
    <FairUseOpsCardShell>
      <p className="text-muted-foreground py-4 text-center text-sm">
        {t("adminDashboard.fairUse.unavailable", "Fair use tracking is not available")}
      </p>
    </FairUseOpsCardShell>
  )
}

/**
 * The month failed to load for a reason the admin needs to see — a 403, a 400,
 * or a 500 from a missing Cosmos composite index. The server's own reason is
 * surfaced; the generic string alone hides which call failed.
 */
export function FairUseOpsError({
  error,
  isFetching,
  onRetry,
}: {
  error: unknown
  isFetching: boolean
  onRetry: () => void
}) {
  const { t } = useTranslation()

  return (
    <FairUseOpsCardShell>
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <div className="bg-destructive/10 rounded-full p-2">
          <AlertCircle className="text-destructive size-6" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium">
            {t("adminDashboard.fairUse.loadErrorTitle", "Could not load fair use data")}
          </p>
          <p className="text-muted-foreground max-w-md text-sm">
            {fairUseErrorDescription(error, t) ??
              t(
                "adminDashboard.fairUse.loadErrorDescription",
                "Something went wrong while fetching this month's usage."
              )}
          </p>
        </div>
        <Button className="h-10" variant="outline" onClick={onRetry}>
          <RefreshCw className={`mr-2 size-4 ${isFetching ? "animate-spin" : ""}`} />
          {t("adminDashboard.fairUse.retry", "Try again")}
        </Button>
      </div>
    </FairUseOpsCardShell>
  )
}
